import { Alignment } from '../../planogram/models/alignment';
import { Injectable } from '@angular/core';
import { Point } from '../models/point';
import { Stand } from '../../plan/models/stand';
import { Utils } from './utils.service';
import { Observable, of } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { HttpService } from './http.service';
import { IManagerExecutedPlanograms } from '../../tasks/models/executedPlanograms';
import { IManagerPlanogramsOnExecution } from '../../tasks/models/planogramsOnExecution';
import { ITableData } from '../models/table';
import { IPlanogramListItem } from '../../planogram/models/planogramListItem';
import { IPlanogram } from '../../planogram/models/planogram';
import { IProductConf } from '../../product/models/models';
import { Router } from '@angular/router';
import { SessionService } from './session.service';
import { SnackBarService } from './snack-bar.service';
import { Equipment } from '../../plan/models/equipment';
import { DwUsersActivityService } from './dw-users-activity.service';
import { DwUserActivityNames } from '../models/dwUserActivityNames';
import { DwUserActivityTypes } from '../models/dwUsersActivityTypes';
import { POSMaterial } from '../../planogram/models/POSMaterials/POSMaterial';
import { POSImageMaterial, POSMaterialImageSaveObject } from '../../planogram/models/POSMaterials/POSImageMaterial';
import { POSMaterialTextSaveObject, POSTextMaterial } from '../../planogram/models/POSMaterials/POSTextMaterial';
import { ITableSettingColumnsData } from '../models/tableSettingColumn';
import { NOT_CANCELLED_HEADER } from '../interceptors/auth.interceptor';

type QueryParamsGetStand = {
	planogram_id?: number;
	only_laid_products?: boolean;
}

export interface StandConf {
	products: IProductConf[];
	blocks: any[];
	pos_materials: Array<Partial<POSMaterialImageSaveObject>>;
	text_materials: Array<Partial<POSMaterialTextSaveObject>>;
	instrument: number;
	linear_meters: number;
	name: string;
	alignment?: Alignment | '';
	arbitrary: boolean;
	conf?: any;
}

export interface IStandConversationMessage {
	body: string;
	conf: any;
	done: boolean;
	done_at: string;
	files: Array<any>;
	id: number;
	read: boolean;
	stand: number;
	stand_version: number;
	title: string;
	type: string;
	user: {
		id: number;
		photo: string;
		full_name: string;
	};
	users: Array<{ id: number; full_name: string }>;
}

export interface IStandConversationDone {
	id: number;
	done: boolean;
}

export interface IStandConversationImage {
	stand_conversation_id: number;
	files: Array<any>;
}

export interface IEquipmentsDownloadMessage {
	name: string;
	url: string;
	current: number;
	total: number;
	key: string;
	file_extension: 'pdf' | 'xls' | 'zip';
	started_at: string;
}

type ReturnForExecutionData = {
	files: Array<any>;
	comment: string;
	deadline_date: string;
	enable_time_tracking?: boolean;
}

const ROOT_URL: string = 'api/layout/stands/',
	NEW_ROOT_URL: string = 'api/v1/planograms/',
    ANALYTICS_ROOT_URL: string = 'api/v1/reports/planogram-analytics';

export interface IShopMarkersItem {
	main_planogram_id: number;
	shop_markers: Array<string>;
}

@Injectable({
	providedIn: 'root'
})
export class PlanogramsService {
	listLoadParams: string = null;

	constructor(
		private router: Router,
		private http: HttpClient,
		private httpService: HttpService,
		private sessionService: SessionService,
		private snackBarService: SnackBarService,
		private dwUsersActivityService: DwUsersActivityService
	) {}

	async bulkCreate(data: Array<any>, planId): Promise<any> {
		if (!data.length) return;

		// backend reorders items in response & always returns stands whose main_stand is null first,
		// so we need to sort the same way to avoid bugs in bulkCreatePlanogramStand()
		const standsToCreate = data.filter(s => !s.id).sort((a, b) => a.main_stand || 0 - b.main_stand || 0);

		if (standsToCreate.length) {
			const created = await new Promise<any>((resolve, reject) => {
				this.http.post<any>('api/v2/planograms/bulk-create/', standsToCreate.map(e => {
					e.id = null;
					e.connectionId = null;

					return {
						name: e.name,
						number: e.number,
						equipment_id: e.instrument.id,
						main_planogram_id: e.main_stand,
						categories: e.categories,
						type: e.type,
						style_conf: {
							color: e.colorBorder,
							fontSize: e.fontSize,
							fontColor: e.fontColor,
							backgroundColor: e.backgroundColor,
							colorBorder: e.colorBorder
						}
					};
				}), { headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true') })
					.subscribe({
						next: (response: any) => resolve(response),
						error: (err: Error) => reject(err)
					});
			});

			standsToCreate.forEach((s, idx) => {
				s.id = created.results[idx].id;
				s.number = created.results[idx].number;
				s.main_stand = created.results[idx].main_planogram;
			});
		}

		const connectionObj = await this.bulkCreatePlanogramStand(data.map(element => {
			return {
				position_x: element.x,
				position_y: element.y,
				rotation: element.angle,
				plan_id: planId,
				planogram_id: element.id,
				client_updated_at: element.planogramStand?.[0]?.client_updated_at
			};
		}));

		data.forEach(s => {
			const conn = connectionObj.results.find(c => c.planogram_id == s.id);
			s.connectionId = (conn || {}).id;
			s.style_conf = (conn || {}).style_conf;
		});

		this.dwUsersActivityService.track(DwUserActivityNames.Planograms, DwUserActivityTypes.Create);
	}

	bulkCreatePlanogramStand(data: any): Promise<any> {
		return new Promise<any>((resolve, reject) => {
			this.http
				.post<any>('api/v1/plan-planograms/bulk-create/', data, { headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true') })
				.subscribe({
					next: res => resolve(res),
					error: (err: HttpErrorResponse) => {
						if (err.status === 500) {
							this.snackBarService.setSnackBarNotification({
								messageText: 'PLAN_EDIT.SAVE_500',
								durationSeconds: 0,
								closeButton: true
							});
						}
						reject(err);
					}
				});
		});
	}

	bulkDelete(ids: Array<number>): Observable<void> {
		if (!ids?.length) return;

		this.dwUsersActivityService.track(DwUserActivityNames.Planograms, DwUserActivityTypes.Delete);

		return this.http.delete<void>('api/v1/plan-planograms/', {
			params: new HttpParams({
				fromObject: {
					ids: ids.join(',')
				}
			}),
            headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true')
		});
	}

	bulkExecutions(data: any): Promise<any> {
		this.dwUsersActivityService.track(DwUserActivityNames.Control, DwUserActivityTypes.SendToExecution);

		return this.httpService.getPromise<any>(
			this.http.post<any>(ROOT_URL + 'bulk_executions/', data)
		);
	}

	bulkFetch(params: Array<{ planogram_id: number; plan_id?: number | null }>): Observable<Array<any>> {
		return this.http.post<any>(`${NEW_ROOT_URL}bulk-fetch/`, params);
	}

	bulkUpdate(params: Array<StandConf>): Observable<Array<StandConf>> {
		this.dwUsersActivityService.track(DwUserActivityNames.Planograms, DwUserActivityTypes.Edit);

		return this.http.post<any>(`api/v2/planograms/bulk-update/`, params);
	}

	cancelExecutions(executions: Array<{ main_stand_id: number, planogram_id: number }>): Promise<any> {
		this.dwUsersActivityService.track(DwUserActivityNames.Control, DwUserActivityTypes.CancelExecution);

		return this.httpService.getPromise<any>(
			this.http.post<any>(NEW_ROOT_URL + 'cancel-executions/', executions)
		);
	}

	checkPhotoReports(planogramIds: Array<number>): Promise<void> {
		this.dwUsersActivityService.track(DwUserActivityNames.Control, DwUserActivityTypes.ConfirmExecution);

		return this.httpService.getPromise<void>(
			this.http.post<void>('/api/layout/stands/bulk_check/', { stand_ids: planogramIds })
		);
	}

	async createEmpty(name: string, equipmentId: number, point: Point, planIds: Array<number>): Promise<any> {
		const stand: any = await this.httpService.getPromise<any>(
			this.http.post<any>('api/v2/planograms/', {
				name: name,
				instrument: equipmentId,
				products: [],
				blocks: [],
                pos_materials: [],
                text_materials: [],
				conf: {}
			})
		);

		await this.makeSvg([{
			main_stand_id: stand.id,
			article_photo_mode: false,
			view_mode: 'direct',
			planogram_id: planIds.length === 1 ? planIds[0] : null
		}]);

		const createPLanogramStandData = planIds.map((planId: number) => ({
			position_x: point.x,
			position_y: point.y,
			rotation: 0,
			plan_id: planId,
			planogram_id: stand.id
		}));

		await this.bulkCreatePlanogramStand(createPLanogramStandData);
		this.dwUsersActivityService.track(DwUserActivityNames.Planograms, DwUserActivityTypes.Create);

		return stand;
	}

	async createFromLibrary(stand: any, planId: number, create_planogram_stand: boolean = true): Promise<any> {
		if (!stand.id) {
			const standObj = await this.httpService.getPromise<any>(
				this.http.post<any>('api/v2/planograms/', {
					name: stand.name,
					number: stand.number,
					instrument: stand.instrument.id,
					old_stand_id: stand.old_stand_id,
					categories: stand.categories,
					style_conf: {
						color: stand.colorBorder,
						fontSize: stand.fontSize,
						fontColor: stand.fontColor,
						backgroundColor: stand.backgroundColor,
						colorBorder: stand.colorBorder
					},
					type: stand.type,
					products: stand.products || [],
					alignment: stand.alignment,
					arbitrary: stand.arbitrary,
					blocks: stand.blocks || [],
                    pos_materials: stand.pos_materials || [],
                    text_materials: stand.text_materials || [],
					conf: stand.conf || {}
				})
			);

			stand.id = standObj.id;
			stand.number = standObj.number;
			stand.main_stand = standObj.main_stand;
			stand.scheduled_date = standObj.scheduled_date;

			await this.makeSvg([{
				main_stand_id: stand.id,
				article_photo_mode: false,
				view_mode: 'direct',
				planogram_id: planId
			}]);
		}

		if (create_planogram_stand) {
			const connectionObj = await this.httpService.getPromise(
				this.http.post('api/v1/plan-planograms/', {
					position_x: stand.x,
					position_y: stand.y,
					rotation: stand.angle,
					plan: planId,
					planogram: stand.id
				}, { headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true') })
			);

			stand.connectionId = connectionObj.id;
		}
		return stand;
	}

	createIndicator(params: any): Observable<void> {
		return this.http.post<void>('api/v2/planogram-indicators/', params);
	}
	createIndicators(params: Array<any>): Observable<void> {
		return this.http.post<void>('api/v2/planogram-indicators/bulk-create/', params);
	}

	dashboardExecutions(): Promise<any> {
		return this.httpService.getPromise<any>(
			this.http.get<any>(ROOT_URL + 'task_manager_executions/')
		);
	}

	delete(stand: Stand | any): Promise<any> {
		this.dwUsersActivityService.track(DwUserActivityNames.Planograms, DwUserActivityTypes.Delete);

		return this.httpService.getPromise<any>(
			this.http.delete<any>(
                `api/v1/plan-planograms/${stand.connectionId}/`, 
                { observe: 'response', headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true') }
            )
		);
	}

	deleteEquipment(id: string): Observable<void> {
		return this.http.delete<void>(`api/layout/equipment-downloads/${id}/`);
	}

	deleteIndicator(id: number): Observable<void> {
		return this.http.delete<void>(`api/v2/planogram-indicators/${id}/`);
	}

	downloadEquipments(params: any): Observable<void> {
		return this.http.post<void>('api/layout/stands/download-equipment/', null, {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	executionDone(stand: any, id: number): Promise<any> {
		this.dwUsersActivityService.track(DwUserActivityNames.Control, DwUserActivityTypes.ExecutionDone);

		return this.httpService.getPromise<any>(
			this.http.post<any>(ROOT_URL + `${id}/execution_done/`, stand)
		);
	}

	get(planId?: number): Promise<any> {
		return this.httpService.getPromise(
			this.http.get(ROOT_URL, {
				params: new HttpParams({
					fromObject: planId ? { planogram_id: planId } : {}
				})
			})
		);
	}

	getShopMarkers(mainPlanogramIds: Array<number>): Observable<Array<IShopMarkersItem>> {
		if (!mainPlanogramIds?.length) return of([]);
		return this.http.get<Array<IShopMarkersItem>>(NEW_ROOT_URL + 'shop-markers/', {
			params: new HttpParams({ fromObject: { main_planogram_ids: mainPlanogramIds.join(',') } })
		}).pipe(catchError(() => of([])));
	}

	getAbcReport(filters: any): Promise<any> {
		return this.httpService.getPromise<any>(
			this.http
				.post<any>('/api/analytics/stand/abc/', filters)
				.pipe(map((response: any) => response.result || {}))
		);
	}

	getAllStands(): Promise<any> {
		return this.httpService.getPromise<any>(
			this.http.post<any>('/api/layout/planogram-stand/show_related_planograms/', {})
		);
	}

	// get stand conf to save
	// fields - an object with fields that are different from the usual or are additional
	getConf(stand: IPlanogram, products: IProductConf[], fields?: any): StandConf {
		const POSMaterials: Array<POSImageMaterial> = stand.stand?.POSMaterials
			.filter((material: POSMaterial): material is POSImageMaterial => material instanceof POSImageMaterial) || [];
		const textMaterials: Array<POSTextMaterial> = stand.stand?.POSMaterials
			.filter((material: POSMaterial): material is POSTextMaterial => material instanceof POSTextMaterial) || [];

		const conf: StandConf = {
			alignment: stand.stand?.alignment == 'default' ? null : stand.stand?.alignment,
			arbitrary: stand.stand?.arbitraryLayout,
			products: products,
			blocks: stand.stand?.blocks,
			pos_materials: POSMaterials.map((material: POSImageMaterial) => material.planogramSaveObject()),
			text_materials: textMaterials.map((material: POSTextMaterial) => material.planogramSaveObject()),
			instrument: stand.stand?.id,
			name: stand.name,
			linear_meters: stand.stand?.LinearMeters,
			conf: {
				...stand.conf,
				layout_meters: stand.stand?.layoutMeters
			}
		};

		if (fields) Utils.assignObject(conf, fields);

		return conf;
	}

	getDefaultSvgConf(stands: Array<IPlanogram>) {
		return stands.map((stand: IPlanogram) => ({
			main_stand_id: stand.main_stand,
			article_photo_mode: false,
			view_mode: 'direct',
			planogram_id: stand.planogram_id
		}));
	}

	getDefaultReport(filters: any): Promise<any> {
		return this.httpService.getPromise<any>(
			this.http
				.post<any>('/api/analytics/stand/base/', filters)
				.pipe(map((response: any) => response.results || {}))
		);
	}

	getEquipments(): Observable<Array<IEquipmentsDownloadMessage>> {
		return this.http.get<Array<IEquipmentsDownloadMessage>>('api/layout/equipment-downloads/');
	}

	getJobs(): Observable<Array<any>> {
		return this.http.get<Array<any>>('/api/v1/jobs/');
	}

	deleteJob(id: string): Observable<void> {
		return this.http.delete<void>(`/api/v1/jobs/${id}/`);
	}

	getExecutionData(params: any = {}): Promise<any> {
		return this.httpService.getPromise<any>(
			this.http.get<any>(NEW_ROOT_URL + 'multi-executions/', {
				params: new HttpParams({
					fromObject: params
				})
			})
		);
	}

	getItem(id: number, params?: QueryParamsGetStand): Promise<any> {
		if (params) Object.keys(params).forEach(key => params[key] === undefined ? delete params[key] : {});
		return this.httpService.getPromise(
			this.http.get(ROOT_URL + `${id}/`, {
				params: new HttpParams({
					fromObject: { ...params, version: 2 }
				})
			})
		);
	}

	getItem$(id: number, params?: QueryParamsGetStand): Observable<any> {
		if (params) Object.keys(params).forEach(key => params[key] === undefined ? delete params[key] : {});
		return this.http
			.get(ROOT_URL + `${id}/`, {
				params: new HttpParams({
					fromObject: { ...params, version: 2 }
				})
			})
			.pipe(
				catchError((error: HttpErrorResponse) => {
					if (error.status === 404) {
						this.router.navigate(this.sessionService.getLink(['stand', 'list'])).then(() => {
							this.snackBarService.setSnackBarNotification({
								messageText: 'COMMON.PLANOGRAM.NOT_EXIST'
							});
						});
					}
					return of(null)
				}),
				map((planogram: any) => {
					if (!planogram) return null;

					if (planogram.locked) {
						this.router.navigate(this.sessionService.getLink(['stand', 'list'])).then(() => {
							this.snackBarService.setSnackBarNotification({
								messageText: 'COMMON.PLANOGRAM.LOCKED'
							});
						});

						return null;
					}

					if (!planogram.planogram_stand?.map(p => Number(p.planogram_id)).includes(Number(params?.planogram_id))) {
						if (!this.router.url.includes('stand/list')) {
							this.router.navigate(this.sessionService.getLink(['stand', 'list'])).then(() => {
								this.snackBarService.setSnackBarNotification({
									messageText: 'COMMON.PLANOGRAM.NOT_EXIST'
								});
							});
						}

						return null;
					}

					return planogram;
				})
			);
	}

    getItem$$(id: number, params?: QueryParamsGetStand): Observable<any> {
		if (params) Object.keys(params).forEach(key => params[key] === undefined ? delete params[key] : {});
		return this.http
			.get(ROOT_URL + `${id}/`, {
				params: new HttpParams({
					fromObject: { ...params, version: 2 }
				})
			});
	}

	getLibrary(params?: any): Observable<any> {
		return this.http.get(ROOT_URL + 'library/', {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	async getLostSalesReport(filters: any): Promise<any> {
		let response = await this.httpService.getPromise<any>(
			this.http.post<any>('/api/analytics/stand/lost-sales/', filters)
		);
		response = { ...response.results, ...response };
		return response;
	}

	getManagerExecutedPlanograms(params?: any): Observable<ITableData<IManagerExecutedPlanograms>> {
		return this.http.get<ITableData<IManagerExecutedPlanograms>>(
			'/api/v1/reports/manager/executed-planograms/',
			{
				params: new HttpParams({ fromObject: params })
			}
		);
	}

	getManagerPlanogramsOnExecution(params?: any): Observable<ITableData<IManagerPlanogramsOnExecution>> {
		return this.http.get<ITableData<IManagerPlanogramsOnExecution>>(
			'/api/v1/reports/manager/planograms-on-execution/',
			{
				params: new HttpParams({ fromObject: params })
			}
		);
	}

	getPlanograms(params: any = {}, fields: Array<string> = []): Observable<Array<any>> {
		if (fields.length) Utils.assignObject(params, { fields: fields.join(',') });
		return this.http.get<Array<any>>(NEW_ROOT_URL, {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	getPlanogramListTable(params: any = {}): Observable<ITableData<IPlanogramListItem>> {
		return this.http.get<ITableData<IPlanogramListItem>>('api/v1/planograms-table/', {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	getRemainsReport(filters: any): Promise<any> {
		return this.httpService.getPromise<any>(
			this.http
				.post<any>('/api/analytics/stand/remains/ ', filters)
				.pipe(map((response: any) => response.result || {}))
		);
	}

	getSalesReport(filters: any): Promise<any> {
		return this.httpService.getPromise<any>(
			this.http
				.post<any>('/api/analytics/stand/', filters)
				.pipe(map((response: any) => response.results || {}))
		);
	}

	getStand(id: number, params?: QueryParamsGetStand): Observable<Stand> {
		return this
			.getItem$(id, params)
			.pipe(
				map(stand => {
					const s: Stand = new Stand(
						stand.planogram_stand[0].position_x,
						stand.planogram_stand[0].position_y,
						new Equipment(stand.instrument),
						stand.locked,
						stand.categories,
						stand.planogram_stand[0].rotation,
						stand.name,
						stand.number,
						stand.main_stand,
						stand.type
					);
		
					s.id = stand.id;
					s.tooltip = s.nameWithNumber();
					s.date = stand.updated_at;
					s.was_sent = stand.was_sent ?? false;
					s.image = stand.image;
		
					if (stand.style_conf.color) s.colorBorder = stand.style_conf.color;
					if (stand.style_conf.fontSize) s.fontSize = stand.style_conf.fontSize;
					if (stand.style_conf.fontColor) s.fontColor = stand.style_conf.fontColor;
					if (stand.style_conf.backgroundColor) s.backgroundColor = stand.style_conf.backgroundColor;
					if (stand.style_conf.colorBorder) s.colorBorder = stand.style_conf.colorBorder;
					if (stand.library) s.library = stand.library;

					s.planogramStand = stand.planogram_stand;
					s.connectionId = (stand.planogram_stand || [])[0].id;
		
					return s;
				})
			);
	}

	async getStandHistory(planogramId: number, planId: number): Promise<any> {
		return this.httpService.getPromise<any>(
			this.http.get<any>(ROOT_URL + `${planogramId}/planogramlife/?planogram_id=${planId}`)
		);
	}

	getSquareReport(filters: any): Promise<any> {
		return this.httpService.getPromise<any>(
			this.http
				.post<any>('/api/analytics/stand/square/', filters)
				.pipe(map((response: any) => response.results || {}))
		);
	}

	getUnLaidProductsOnPlan(data: any) {
		return this.httpService.getPromise(
			this.http.post(`/api/layout/planograms/unlaid-planograms-products/`, data)
		);
	}

	// TODO: need types for svg conf
	makeSvg(conf: Array<any>): Promise<void> {
		return this.httpService.getPromise<void>(
			this.http.post<void>(
                ROOT_URL + 'make_svg/?version=2', 
                conf, 
                { headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true') }
            )
		);
	}

	partialUpdate(stand: any, planId?: number): Promise<any> {
		stand.planogram_id = planId;
		return new Promise<any>((resolve, reject) => {
			this.http.patch(
                `api/v2/planograms/${stand.main_stand}/?plan_id=${planId}`,
                 stand,
                 { headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true') }
            )
				.subscribe({
					next: (res: any) => resolve(res),
					error: (err: Error) => reject(err)
				});
		});
	}

	patchStand(id: number, data: any): Promise<any> {
		this.dwUsersActivityService.track(DwUserActivityNames.Planograms, DwUserActivityTypes.Edit);

		return this.httpService.getPromise<any>(
			this.http.patch<any>(`api/v2/planograms/${id}/`, data, { headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true') })
		);
	}

	returnForExecution(planogramId: number, data: ReturnForExecutionData): Promise<void> {
		this.dwUsersActivityService.track(DwUserActivityNames.Control, DwUserActivityTypes.ReturnForExecution);

		return this.httpService.getPromise<void>(
			this.http.post<void>(`/api/layout/stands/${planogramId}/cancel/`, data)
		);
	}

	revertStand(id: number): Observable<void> {
		this.dwUsersActivityService.track(DwUserActivityNames.Planograms, DwUserActivityTypes.Revert);

		return this.http.post<void>(`api/layout/conversation/${id}/revert/`, {});
	}

	sendPhotoReport(report: any): Promise<any> {
		return this.httpService.getPromise<any>(
			this.http
				.post<any>(`/api/layout/stand-photo-reports/`, report)
				.pipe(map((response: any) => response.results || {}))
		);
	}

	showPhotoReport(executionId: number | string): Promise<any> {
		return this.httpService.getPromise<any>(
			this.http.patch<any>(`api/layout/stand-execution/${executionId}/`, { was_show: true })
		);
	}

	async update(stand: Stand, planId: number, q?: any): Promise<Stand> {
		const data = {
			name: stand.name,
			number: stand.number,
			instrument: stand.instrument.id,
			categories: stand.categories,
			style_conf: {
				color: stand.colorBorder,
				fontSize: stand.fontSize,
				fontColor: stand.fontColor,
				backgroundColor: stand.backgroundColor,
				colorBorder: stand.colorBorder
			},
			type: stand.type
		};

		await new Promise<any>((resolve, reject) => {
			this.http.patch(`api/v2/planograms/${stand.main_stand}/`, data, { headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true') })
				.subscribe({
					next: (res: any) => resolve(res),
					error: (err: Error) => reject(err)
				});
		});

		this.dwUsersActivityService.track(DwUserActivityNames.Planograms, DwUserActivityTypes.Edit);

		return stand;
	}

	async updatePlanPosition(stand: Stand, planId: number): Promise<void> {
		if (stand.connectionId) {
			await new Promise<any>((resolve, reject) => {
				this.http.patch(
                    `api/v1/plan-planograms/${stand.connectionId}/`, 
                    {
                        position_x: stand.x,
                        position_y: stand.y,
                        rotation: stand.angle,
                        client_updated_at: stand.planogramStand?.[0]?.client_updated_at
                    },
                    { headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true') }
                ).subscribe({
                        next: (res: any) => resolve(res),
                        error: (err: Error) => reject(err)
                    });
                });
		}
	}

	updatePlacementShelf(id: number, conf: StandConf): Promise<any> {
		this.dwUsersActivityService.track(DwUserActivityNames.Planograms, DwUserActivityTypes.Edit);

		return new Promise((resolve, reject) => {
			this.http.put<any>(`api/v2/planograms/${id}/`, conf, { headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true') })
                .subscribe({
                    next: (res: any) => resolve(res),
                    error: (err: Error) => reject(err)
                });
		});
	}

	updateStandField(id: number, field: any): Promise<any> {
		return this.httpService.getPromise<any>(
			this.http.patch<any>(`api/v2/planograms/${id}/`, field, { headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true') })
		);
	}

	unlock(id: number): Observable<void> {
		return this.http.post<void>(`${NEW_ROOT_URL}${id}/unlock/`, {});
	}

    /** DISTRIBUTION: api/v1/reports/planogram-analytics/distribution/ */
	getPlanogramDistribution(params: any): Observable<ITableData> {
		return this.http.get<ITableData>(`${ANALYTICS_ROOT_URL}/distribution/`, { params: new HttpParams({ fromObject: params }) });
	}

    /** SQUARE: api/v1/reports/planogram-analytics/square/ */
	getPlanogramSquare(params: any): Observable<ITableData> {
		return this.http.get<ITableData>(`${ANALYTICS_ROOT_URL}/square/`, { params: new HttpParams({ fromObject: params }) });
	}

    /** LOST SALES: api/v1/reports/planogram-analytics/lost-sales/ */
	getPlanogramLostSales(params: any): Observable<ITableData> {
		return this.http.get<ITableData>(`${ANALYTICS_ROOT_URL}/lost-sales/`, { params: new HttpParams({ fromObject: params }) });
	}

	/** ABC: api/v1/reports/planogram-analytics/abc/ */
	getPlanogramAbc(params: any, body: any): Observable<ITableData> {
		return this.http.post<ITableData>(
			`${ANALYTICS_ROOT_URL}/abc/`,
			body,
			{ params: new HttpParams({ fromObject: params }) }
		);
	}

	/** Remains: api/v1/reports/planogram-analytics/remains/ */
	getPlanogramStock(params: any): Observable<ITableData> {
		return this.http.get<ITableData>(`${ANALYTICS_ROOT_URL}/remains/`, { params: new HttpParams({ fromObject: params }) });
	}

	/** DISTRIBUTION COLUMNS: api/v1/reports/planogram-analytics/distribution/columns/ */
	loadColumnsDistribution(params: {
		date_from: string;
		date_to: string;
		plan_id: number | string;
		planogram_id: number | string;
	}): Observable<ITableSettingColumnsData> {
		return this.http.get<ITableSettingColumnsData>(
			`${ANALYTICS_ROOT_URL}/distribution/columns/`,
			{
				params: new HttpParams({ fromObject: {
						date_from: String(params.date_from),
						date_to: String(params.date_to),
						plan_id: String(params.plan_id),
						planogram_id: String(params.planogram_id),
					}})
			}
		);
	}

	/** SQUARE COLUMNS: api/v1/reports/planogram-analytics/square/columns/ */
	loadColumnsSquare(params: {
		date_from: string;
		date_to: string;
		plan_id: number | string;
		planogram_id: number | string;
	}): Observable<ITableSettingColumnsData> {
		return this.http.get<ITableSettingColumnsData>(
			`${ANALYTICS_ROOT_URL}/square/columns/`,
			{
				params: new HttpParams({
					fromObject: {
						date_from: String(params.date_from),
						date_to: String(params.date_to),
						plan_id: String(params.plan_id),
						planogram_id: String(params.planogram_id)
					}
				})
			}
		);
	}

	/** LOST-SALES COLUMNS: api/v1/reports/planogram-analytics/lost-sales/columns/ */
	loadColumnsLostSales(params: {
		date_from: string;
		date_to: string;
		plan_id: number | string;
		planogram_id: number | string;
	}): Observable<ITableSettingColumnsData> {
		return this.http.get<ITableSettingColumnsData>(
			`${ANALYTICS_ROOT_URL}/lost-sales/columns/`,
			{
				params: new HttpParams({
					fromObject: {
						date_from: String(params.date_from),
						date_to: String(params.date_to),
						plan_id: String(params.plan_id),
						planogram_id: String(params.planogram_id)
					}
				})
			}
		);
	}

	/** ABC COLUMNS: api/v1/reports/planogram-analytics/abc/columns/ */
	loadColumnsAbc(params: {
		date_from: string;
		date_to: string;
		plan_id: number | string;
		planogram_id: number | string;
	}): Observable<ITableSettingColumnsData> {
		return this.http.get<ITableSettingColumnsData>(
			`${ANALYTICS_ROOT_URL}/abc/columns/`,
			{
				params: new HttpParams({
					fromObject: {
						date_from: String(params.date_from),
						date_to: String(params.date_to),
						plan_id: String(params.plan_id),
						planogram_id: String(params.planogram_id)
					}
				})
			}
		);
	}

	/** Remains COLUMNS: api/v1/reports/planogram-analytics/remains/columns/ */
	loadColumnsStock(params: {
		date_from: string;
		date_to: string;
		plan_id: number | string;
		planogram_id: number | string;
	}): Observable<ITableSettingColumnsData> {
		return this.http.get<ITableSettingColumnsData>(
			`${ANALYTICS_ROOT_URL}/remains/columns/`,
			{
				params: new HttpParams({
					fromObject: {
						date_from: String(params.date_from),
						date_to: String(params.date_to),
						plan_id: String(params.plan_id),
						planogram_id: String(params.planogram_id)
					}
				})
			}
		);
	}

	/** DISTRIBUTION TOOLTIP: api/v1/reports/planogram-analytics/distribution-tooltip/ */
	getDistributionTooltip(params: {
		date_from: string;
		date_to: string;
		plan_id: number | string;
		planogram_id: number | string;
		metric?: string;
		distribution?: string;
		discrete_colors?: string[] | string;
		discrete_values?: number[];
		color?: string;
		promo_products?: boolean;
		new_products?: boolean;
	}): Observable<any> {
		const httpParams: any = {
			date_from: String(params.date_from),
			date_to: String(params.date_to),
			plan_id: String(params.plan_id),
			planogram_id: String(params.planogram_id),
		};

		if (params.metric) httpParams.metric = String(params.metric);
		if (params.distribution) httpParams.distribution = String(params.distribution);
		if (params.discrete_colors) {
			if (Array.isArray(params.discrete_colors)) {
				httpParams.discrete_colors = params.discrete_colors.join(',');
			} else {
				httpParams.discrete_colors = String(params.discrete_colors);
			}
		}
		if (params.discrete_values) {
			httpParams.discrete_values = params.discrete_values.join(',');
		}
		if (params.color) httpParams.color = String(params.color);
		if (params.promo_products !== undefined) httpParams.promo_products = String(params.promo_products);
		if (params.new_products !== undefined) httpParams.new_products = String(params.new_products);

		return this.http.get<any>(`${ANALYTICS_ROOT_URL}/distribution-tooltip/`, {
			params: new HttpParams({ fromObject: httpParams })
		});
	}

	/** SQUARE TOOLTIP: api/v1/reports/planogram-analytics/square-tooltip/ */
	getSquareTooltip(params: {
		date_from: string;
		date_to: string;
		x_axis_metric: string;
		y_axis_metric: string;
		plan_id: number | string;
		planogram_id: number | string;
	}, colorValues: any): Observable<any> {
		return this.http.post<any>(`${ANALYTICS_ROOT_URL}/square-tooltip/`, colorValues, {
			params: new HttpParams({ fromObject: {
				date_from: String(params.date_from),
				date_to: String(params.date_to),
				x_axis_metric: String(params.x_axis_metric),
				y_axis_metric: String(params.y_axis_metric),
				plan_id: String(params.plan_id),
				planogram_id: String(params.planogram_id),
			}})
		});
	}

	/** LOST-SALES TOOLTIP: api/v1/reports/planogram-analytics/lost-sales-tooltip/ */
	getLostSalesTooltip(params: {
		date_from: string;
		date_to: string;
		plan_id: number | string;
		planogram_id: number | string;
	}): Observable<any> {
		return this.http.get<any>(`${ANALYTICS_ROOT_URL}/lost-sales-tooltip/`, {
			params: new HttpParams({ fromObject: {
				date_from: String(params.date_from),
				date_to: String(params.date_to),
				plan_id: String(params.plan_id),
				planogram_id: String(params.planogram_id),
			}})
		});
	}

	/** ABC TOOLTIP: api/v1/reports/planogram-analytics/abc-tooltip/ */
	getAbcTooltip(params: {
		date_from: string;
		date_to: string;
		plan_id: number | string;
		planogram_id: number | string;
		avg_by_stores?: boolean;
	}, body: any): Observable<any> {
		const httpParams: any = {
			date_from: String(params.date_from),
			date_to: String(params.date_to),
			plan_id: String(params.plan_id),
			planogram_id: String(params.planogram_id),
		};

		if (params.avg_by_stores !== undefined) httpParams.avg_by_stores = String(params.avg_by_stores);

		return this.http.post<any>(`${ANALYTICS_ROOT_URL}/abc-tooltip/`, body, {
			params: new HttpParams({ fromObject: httpParams })
		});
	}

	/** REMAINS TOOLTIP: api/v1/reports/planogram-analytics/remains-tooltip/ */
	getStockTooltip(params: {
		date_from: string;
		date_to: string;
		plan_id: number | string;
		planogram_id: number | string;
		interval?: number;
		remains_border_percent?: number;
		remains_high_color?: string;
		remains_low_color?: string;
		surplus_border_percent?: number;
		surplus_high_color?: string;
		surplus_low_color?: string;
	}): Observable<any> {
		const httpParams: any = {
			date_from: String(params.date_from),
			date_to: String(params.date_to),
			plan_id: String(params.plan_id),
			planogram_id: String(params.planogram_id),
		};

		if (params.interval !== undefined) httpParams.interval = String(params.interval);
		if (params.remains_border_percent !== undefined) httpParams.remains_border_percent = String(params.remains_border_percent);
		if (params.remains_high_color) httpParams.remains_high_color = String(params.remains_high_color);
		if (params.remains_low_color) httpParams.remains_low_color = String(params.remains_low_color);
		if (params.surplus_border_percent !== undefined) httpParams.surplus_border_percent = String(params.surplus_border_percent);
		if (params.surplus_high_color) httpParams.surplus_high_color = String(params.surplus_high_color);
		if (params.surplus_low_color) httpParams.surplus_low_color = String(params.surplus_low_color);

		return this.http.get<any>(`${ANALYTICS_ROOT_URL}/remains-tooltip/`, {
			params: new HttpParams({ fromObject: httpParams })
		});
	}
}